import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowRight, ExternalLink, Users } from 'lucide-react';
import axios from 'axios';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { projectDetails } from '../data/projectDetails';
import { TryoutRegistrationForm } from '../components/projects/TryoutRegistrationForm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProjectDetailPage = ({ project }) => {
  const detail = projectDetails[project.slug];
  const isRightsProject = project.slug === 'rights-of-nature';
  const isSoftballProject = project.slug === 'softball-for-sovereignty';

  const sectionStyle = isSoftballProject
    ? {
        backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.92), rgba(255,255,255,0.94)), url(${detail.backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : { backgroundColor: '#53a5ca10' };

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`project-detail-page-${project.slug}`}>
      <section className="py-16 md:py-24" style={sectionStyle}>
        <div className="container mx-auto px-4 max-w-6xl">
          <Link to="/projects" className="inline-flex items-center gap-2 mb-8 text-sm font-semibold" style={{ color: '#53a5ca' }} data-testid="project-detail-back-link">
            <ArrowLeft className="h-4 w-4" /> Back to all projects
          </Link>

          {isRightsProject ? (
            <div className="overflow-hidden rounded-[36px] shadow-2xl aspect-[5/2] mb-10 bg-white" data-testid="rights-banner-image">
              <img src={detail?.heroImage} alt="Give Rights to Water banner" className="w-full h-full object-cover object-center" />
            </div>
          ) : null}

          <div className={`grid gap-10 items-start ${isRightsProject ? 'lg:grid-cols-1' : 'lg:grid-cols-2'}`}>
            <div className="space-y-5">
              <div className="inline-flex rounded-full px-4 py-2 text-sm font-semibold" style={{ backgroundColor: '#dbbf5720', color: '#dbbf57' }}>
                {detail?.eyebrow || 'Priority Project'}
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight" style={{ color: '#7e2f22' }}>
                {detail?.heroTitle || project.title}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">{detail?.heroSummary || project.longDescription}</p>

              {detail?.documents?.length ? (
                <div className="flex flex-wrap gap-3">
                  {detail.documents.map((document, index) => (
                    <a
                      key={document.url}
                      href={document.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white"
                      style={{ backgroundColor: index === 0 ? '#53a5ca' : '#e37c55' }}
                      data-testid={`project-document-link-${index}`}
                    >
                      {document.title} <ExternalLink className="h-4 w-4" />
                    </a>
                  ))}
                </div>
              ) : null}
            </div>

            {!isRightsProject ? (
              <div className="grid gap-4">
                <div className="overflow-hidden rounded-[28px] shadow-xl bg-white aspect-[4/4]">
                  <img src={detail?.heroImage || project.image} alt={project.title} className="w-full h-full object-cover" />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {detail?.cards?.length ? (
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-6xl grid md:grid-cols-3 gap-6">
            {detail.cards.map((item) => (
              <Card key={item.title} className="border-0 shadow-lg" data-testid={`project-card-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <CardHeader>
                  <CardTitle style={{ color: '#7e2f22' }}>{item.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600 leading-relaxed">{item.body}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>
      ) : null}

      {isSoftballProject ? (
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4 max-w-6xl space-y-10">
            <Card className="border-0 shadow-xl overflow-hidden" data-testid="softball-quote-card">
              <div className="p-8 md:p-10" style={{ background: 'linear-gradient(135deg, #7e2f22 0%, #e37c55 100%)' }}>
                <p className="text-white/70 uppercase tracking-[0.2em] text-xs mb-4">Why this matters</p>
                <blockquote className="text-2xl md:text-4xl font-bold text-white leading-tight">“{detail.spotlightQuote}”</blockquote>
              </div>
            </Card>

            <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-5">
              {detail.gallery.map((image, index) => (
                <div key={image} className="rounded-[24px] overflow-hidden shadow-lg aspect-[4/3] bg-white" data-testid={`softball-gallery-image-${index}`}>
                  <img src={image} alt={`Softball for Sovereignty moment ${index + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>

            <div className="grid lg:grid-cols-3 gap-6 items-start">
              {detail.sections.map((section) => (
                <Card key={section.title} className="border-0 shadow-lg h-full" data-testid={`softball-section-${section.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <CardHeader>
                    <CardTitle style={{ color: '#7e2f22' }}>{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-gray-600 leading-relaxed">
                      {section.bullets.map((bullet) => <li key={bullet}>• {bullet}</li>)}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="space-y-6">
              <div className="max-w-3xl">
                <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#7e2f22' }}>Upcoming tryouts</h2>
                <p className="text-lg text-gray-600">Watch the preview and pre-register side by side for the next Softball for Sovereignty tryout cycle.</p>
              </div>

              <div className="grid lg:grid-cols-2 gap-6 items-start">
                <Card className="border-0 shadow-xl overflow-hidden" data-testid="softball-video-card">
                  <CardHeader>
                    <CardTitle className="text-lg" style={{ color: '#7e2f22' }}>Tryout preview</CardTitle>
                    <CardDescription>Quick video from the NAIWA Daughters softball program.</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <video controls className="w-full rounded-[20px] bg-black max-h-[360px] object-cover" data-testid="softball-tryout-video">
                      <source src={detail.tryoutVideo} type="video/mp4" />
                    </video>
                  </CardContent>
                </Card>

                <TryoutRegistrationForm />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {isRightsProject ? (
        <>
          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 max-w-6xl space-y-10">
              <div className="text-center max-w-3xl mx-auto">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Rights acknowledged for Longperson</h2>
                <p className="text-lg text-gray-600">These rights are presented prominently because they sit at the heart of the Cherokee-led Rights of Nature framework.</p>
              </div>

              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {detail.rights.map((item, index) => (
                  <Card key={item} className="border-0 shadow-xl bg-stone-50" data-testid={`rights-acknowledged-card-${index}`}>
                    <CardHeader>
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center text-white font-bold" style={{ backgroundColor: index % 2 === 0 ? '#53a5ca' : '#e37c55' }}>
                        {String(index + 1).padStart(2, '0')}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-700 leading-relaxed text-base">{item}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle style={{ color: '#7e2f22' }}>How people can support</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 text-gray-600 leading-relaxed">
                    {detail.actionItems.map((item) => <li key={item}>• {item}</li>)}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </section>

          <section className="py-8 bg-white">
            <div className="container mx-auto px-4 max-w-5xl">
              <div className="overflow-hidden rounded-[32px] shadow-2xl aspect-[5/2] bg-white" data-testid="rights-supporting-image">
                <img src={detail.supportingImage} alt="NAIWA Daughters members in the water" className="w-full h-full object-cover object-center" />
              </div>
            </div>
          </section>

          <section className="py-16" style={{ backgroundColor: '#dbbf5720' }}>
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>Rights of Nature FAQ</h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {detail.faq.map((item, index) => (
                  <Card key={item.question} className="border-0 shadow-lg" data-testid={`rights-faq-card-${index}`}>
                    <CardHeader>
                      <CardTitle className="text-xl" style={{ color: '#7e2f22' }}>{item.question}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600 leading-relaxed">{item.answer}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 bg-white">
            <div className="container mx-auto px-4 max-w-6xl space-y-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#7e2f22' }}>In the field and in community</h2>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                {detail.facebookEmbeds.map((embed, index) => (
                  <div key={embed} className="rounded-[28px] overflow-hidden shadow-lg bg-white p-2" data-testid={`rights-facebook-embed-${index}`}>
                    <iframe
                      src={embed}
                      width="100%"
                      height="260"
                      style={{ border: 'none', overflow: 'hidden' }}
                      scrolling="no"
                      frameBorder="0"
                      allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      allowFullScreen
                      title={`Rights of Nature Facebook post ${index + 1}`}
                    />
                  </div>
                ))}
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {detail.gallery.map((image, index) => (
                  <div key={image.src} className="group rounded-[28px] overflow-hidden shadow-lg aspect-[4/3] bg-white relative" data-testid={`rights-gallery-image-${index}`}>
                    <img src={image.src} alt={`Rights of Nature activity ${index + 1}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" style={{ objectPosition: image.objectPosition }} />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-5">
                      <p className="text-white text-sm leading-relaxed font-medium">{image.caption}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
};

const Projects = () => {
  const { slug } = useParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects`);
        setProjects(response.data);
      } catch (error) {
        console.error('Error fetching projects:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, []);

  const project = useMemo(() => projects.find((item) => item.slug === slug), [projects, slug]);

  if (slug) {
    if (loading) {
      return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="project-detail-loading-state">Loading project…</div>;
    }

    if (!project) {
      return <div className="min-h-screen flex items-center justify-center text-gray-500" data-testid="project-detail-empty-state">Project not found.</div>;
    }

    return <ProjectDetailPage project={project} />;
  }

  return (
    <div className="min-h-screen">
      <section className="py-16 md:py-24" style={{ backgroundColor: '#53a5ca10' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Our Priority Projects
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Empowering Indigenous young women through meaningful initiatives that combine cultural preservation, leadership development, and community impact.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading projects...</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {projects.map((projectItem) => (
                <Card key={projectItem.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300" data-testid={`project-card-${projectItem.slug}`}>
                  <Link to={`/projects/${projectItem.slug}`} data-testid={`project-image-link-${projectItem.slug}`}>
                    <div className="relative h-64 overflow-hidden cursor-pointer">
                      <img src={projectDetails[projectItem.slug]?.heroImage || projectItem.image} alt={projectItem.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-300" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <div className="flex items-center justify-between">
                          <span className="px-3 py-1 rounded-full text-xs font-semibold text-white" style={{ backgroundColor: projectItem.status === 'Active' ? '#53a5ca' : '#dbbf57' }}>
                            {projectItem.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </Link>

                  <CardHeader>
                    <CardTitle className="text-2xl" style={{ color: '#7e2f22' }}>{projectItem.title}</CardTitle>
                    <CardDescription className="text-base">{projectItem.longDescription}</CardDescription>
                  </CardHeader>

                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2 text-sm">
                        <Users className="h-4 w-4" style={{ color: '#53a5ca' }} />
                        <span className="font-semibold" style={{ color: '#53a5ca' }}>{projectItem.impact}</span>
                      </div>

                      <Link to={`/projects/${projectItem.slug}`}>
                        <Button className="w-full text-white hover:opacity-90 transition-all duration-200" style={{ backgroundColor: '#e37c55' }} data-testid={`project-detail-link-${projectItem.slug}`}>
                          Learn More About This Project
                          <ArrowRight className="ml-2 h-5 w-5" />
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Want to Get Involved?
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join us in making a difference through our priority projects and community initiatives.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/join">
                <Button size="lg" className="text-white hover:opacity-90 transition-all duration-200" style={{ backgroundColor: '#53a5ca' }}>
                  Join Naiwa Daughters
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/support">
                <Button size="lg" variant="outline" className="border-2" style={{ borderColor: '#e37c55', color: '#e37c55' }}>
                  Support Our Projects
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Projects;